import classnames from 'classnames';
import I18n from 'i18n-js';
import React, { useContext } from 'react';

import { RCTag, RCToggledContent, TagType } from '@reverbdotcom/cadence/components';
import { inMobileApp } from '@reverbdotcom/commons/src/user_context_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

import { SellFormContext } from '../SellFormContext';
import SellFormSectionButtons, { ButtonType } from './SellFormSectionButtons';

function SellFormSectionCard({ children }) {
  return (
    <div className="sell-form-section__card">
      {children}
    </div>
  );
}

interface IToggledSectionProps {
  title: string;
  children: React.ReactNode;
  id: string;
  defaultOpen?: boolean;
  additionalHeadingContent?: React.ReactNode;
}

function ToggledSection({
  title,
  children,
  id,
  defaultOpen = false,
  additionalHeadingContent,
}: IToggledSectionProps) {
  return (
    <FormSection togglable>
      <RCToggledContent
        id={id}
        heading={title}
        defaultOpen={defaultOpen}
        additionalHeadingContent={additionalHeadingContent}
      >
        {children}
      </RCToggledContent>
    </FormSection>
  );
}
interface IFormSectionProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  tag?: TagType;
  includeBottomBorder?: boolean;
  fullWidthForm?: boolean;
  togglable?: boolean;
}

function FormSection({
  title,
  subtitle,
  children,
  tag,
  includeBottomBorder = false,
  fullWidthForm = false,
  togglable = false,
}: IFormSectionProps) {
  const classes = classnames(
    'sell-form-section__form-section',
    {
      'sell-form-section__form-section--bottom-border': includeBottomBorder,
      'sell-form-section__form-section--condensed-form': !fullWidthForm,
      'sell-form-section__form-section--toggled-section': togglable,
    },
  );

  return (
    <div className={classes}>
      {title && (
        <div className="sell-form-section__form-section__header">
          <span className="sell-form-section__form-section__header__title">
            {title}
          </span>
          {tag && tag !== 'none' && (
            <div className="sell-form-section__form-section__header__tag">
              <RCTag variant={tag === 'recommended' ? 'highlight' : null}>
                {I18n.t(`commons.formGroup.${tag}`)}
              </RCTag>
            </div>
          )}

          {subtitle && (
            <p className="sell-form-section__form-section__header__subtitle">
              {subtitle}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

interface IProps {
  children: React.ReactNode;
  onSubmit: () => void;
  onPreviousButtonClick: () => void;
  onDiscardButtonClick: () => void;
  additionalFooterContent?: React.ReactNode;
  disableSubmit?: boolean;
  scrollRef?: React.MutableRefObject<HTMLDivElement>;
  headerButtons: ButtonType[];
  footerButtons: ButtonType[];
}

export function SellFormSection({
  children,
  onSubmit,
  onPreviousButtonClick,
  onDiscardButtonClick,
  additionalFooterContent,
  disableSubmit = false,
  scrollRef,
  headerButtons,
  footerButtons,
}: IProps) {
  const { loading, setShowRequiredFieldError } = useContext(SellFormContext);
  const showHeaderButtons = headerButtons.length > 0;

  const footerClasses = classnames(
    'sell-form-section__footer',
    {
      'sell-form-section__footer--submit-disabled': loading || disableSubmit,
      'sell-form-section__footer--native-mobile': inMobileApp(useUser()),
    },
  );

  return (
    <form
      className="sell-form-section"
      onSubmit={(e) => {
        e.preventDefault();
        if (!loading && !disableSubmit) {
          onSubmit();
        } else if (scrollRef?.current) {
          setShowRequiredFieldError(true);
          scrollRef.current.scrollIntoView();
        }
      }}
    >
      {showHeaderButtons && (
        <div className="sell-form-section__header__button__row">
          <div className="sell-form-section__header__buttons__main">
            <SellFormSectionButtons
              buttons={headerButtons}
              disableSubmit={disableSubmit}
              onPreviousButtonClick={onPreviousButtonClick}
              onDiscardButtonClick={onDiscardButtonClick}
            />
          </div>
        </div>
      )}

      <div className="sell-form-section__form-fields-container">
        {children}
      </div>

      <div className="sell-form-section__mobile-additional-content">
        {additionalFooterContent}
      </div>

      <div className={footerClasses}>
        <div className="sell-form-section__footer__additional-content">
          {additionalFooterContent}
        </div>

        {footerButtons.length > 0 && (
          <div className="sell-form-section__footer__actions">
            <SellFormSectionButtons
              buttons={footerButtons}
              disableSubmit={disableSubmit}
              mobileVerticalAlign={footerButtons.includes(ButtonType.DISCARD)}
              onPreviousButtonClick={onPreviousButtonClick}
              onDiscardButtonClick={onDiscardButtonClick}
            />
          </div>
        )}
      </div>
    </form>
  );
}

SellFormSection.FormSection = FormSection;
SellFormSection.Card = SellFormSectionCard;
SellFormSection.ToggledSection = ToggledSection;
